import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSearch, FaUserPlus, FaPen, FaTrashAlt, FaPlus } from 'react-icons/fa';
import Pagination, { PaginationMeta } from './Pagination';
import { useAuth } from '../../context/AuthContext';
import styles from './styles/Clientes.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type TabKey = 'clientes' | 'cuenta-corriente';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'clientes', label: 'Clientes' },
    { key: 'cuenta-corriente', label: 'Cuenta Corriente' },
];

interface Cliente {
    dni: string;
    nombre: string;
    apellido: string;
    email: string;
    telefono: string;
    direccion: string;
    saldo_cc: number;
    compras: number;
    facturado: number;
    ultima_compra: string | null;
}

/** Movimiento de cuenta corriente. Positivo = a favor; negativo = deuda. */
interface Movimiento {
    id: number;
    fecha: string;
    tipo: string;
    concepto: string;
    importe: number;
    usuario: string;
}

/** Campos editables del cliente. Coinciden con miembros_familia. */
interface FormCliente {
    dni: string;
    nombre: string;
    apellido: string;
    email: string;
    telefono: string;
    direccion: string;
    fecha_nacimiento: string;
    sexo: string;
    saldo_cc: string;
}

const FORM_VACIO: FormCliente = {
    dni: '', nombre: '', apellido: '', email: '',
    telefono: '', direccion: '', fecha_nacimiento: '', sexo: '', saldo_cc: '',
};

const money = (n: number) => `$${Math.round(n || 0).toLocaleString('es-AR')}`;

/** El saldo se muestra con signo: negativo = el cliente debe. */
const saldoTexto = (n: number) =>
    n === 0 ? '$0' : `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('es-AR')}`;

const iniciales = (c: Cliente) =>
    `${(c.nombre || '').charAt(0)}${(c.apellido || '').charAt(0)}`.toUpperCase() || '–';

const Clientes: React.FC = () => {
    const { user } = useAuth();

    const [tab, setTab] = useState<TabKey>('clientes');
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [toast, setToast] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [seleccionado, setSeleccionado] = useState<Cliente | null>(null);

    // Alta/edicion y movimientos de cuenta corriente.
    const [formOpen, setFormOpen] = useState(false);
    const [editando, setEditando] = useState<Cliente | null>(null);
    const [movOpen, setMovOpen] = useState(false);
    const [movimientos, setMovimientos] = useState<Movimiento[]>([]);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    const notify = useCallback((type: 'ok' | 'error', text: string) => {
        setToast({ type, text });
        window.setTimeout(() => setToast(null), 2600);
    }, []);

    useEffect(() => {
        const id = window.setTimeout(() => setDebounced(search), 300);
        return () => window.clearTimeout(id);
    }, [search]);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (debounced.trim()) params.set('q', debounced.trim());
        try {
            const res = await fetch(`${API}/admin/clientes?${params.toString()}`);
            const data = await res.json();
            setClientes(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error cargando clientes', err);
            setError('No se pudieron cargar los clientes.');
        } finally {
            setLoading(false);
        }
    }, [debounced]);

    useEffect(() => { cargar(); }, [cargar]);
    useEffect(() => { setPage(1); }, [debounced, perPage, tab]);

    /** Los movimientos se piden al abrir la ficha o el modal de cuenta corriente. */
    const cargarMovimientos = useCallback(async (dni: string) => {
        try {
            const res = await fetch(`${API}/admin/clientes/${encodeURIComponent(dni)}/cuenta-corriente`);
            const data = await res.json();
            setMovimientos(Array.isArray(data.movimientos) ? data.movimientos : []);
        } catch (err) {
            console.error('Error cargando cuenta corriente', err);
            setMovimientos([]);
        }
    }, []);

    useEffect(() => {
        if (seleccionado) cargarMovimientos(seleccionado.dni);
        else setMovimientos([]);
    }, [seleccionado, cargarMovimientos]);

    /* ------------------------------------------------------------- acciones */

    const guardarCliente = async (form: FormCliente) => {
        const esNuevo = !editando;
        const url = esNuevo
            ? `${API}/admin/clientes`
            : `${API}/admin/clientes/${encodeURIComponent(editando!.dni)}`;
        try {
            const res = await fetch(url, {
                method: esNuevo ? 'POST' : 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                notify('error', data.error || 'No se pudo guardar el cliente');
                return;
            }
            setFormOpen(false);
            setEditando(null);
            notify('ok', esNuevo ? 'Cliente creado' : 'Cliente actualizado');
            cargar();
        } catch (err) {
            console.error('Error guardando cliente', err);
            notify('error', 'Error de conexión');
        }
    };

    const eliminarCliente = async (c: Cliente) => {
        const ok = window.confirm(
            `¿Eliminar a ${c.nombre} ${c.apellido}?\n\n`
            + 'Se borran también sus movimientos de cuenta corriente. '
            + 'Las ventas ya registradas no se tocan.'
        );
        if (!ok) return;
        try {
            const res = await fetch(`${API}/admin/clientes/${encodeURIComponent(c.dni)}`, {
                method: 'DELETE',
            });
            if (!res.ok) {
                notify('error', 'No se pudo eliminar el cliente');
                return;
            }
            if (seleccionado?.dni === c.dni) setSeleccionado(null);
            notify('ok', 'Cliente eliminado');
            cargar();
        } catch (err) {
            console.error('Error eliminando cliente', err);
            notify('error', 'Error de conexión');
        }
    };

    const registrarMovimiento = async (mov: { tipo: string; importe: string; concepto: string }) => {
        if (!seleccionado) return;
        try {
            const res = await fetch(
                `${API}/admin/clientes/${encodeURIComponent(seleccionado.dni)}/cuenta-corriente`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...mov, usuario: user || '' }),
                },
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                notify('error', data.error || 'No se pudo registrar el movimiento');
                return;
            }
            setMovOpen(false);
            notify('ok', 'Movimiento registrado');
            await cargarMovimientos(seleccionado.dni);
            cargar();
            // El saldo del panel se actualiza con el que devolvio el backend.
            setSeleccionado(prev => (prev ? { ...prev, saldo_cc: data.saldo_cc ?? prev.saldo_cc } : prev));
        } catch (err) {
            console.error('Error registrando movimiento', err);
            notify('error', 'Error de conexión');
        }
    };

    /* -------------------------------------------------------------- derivados */

    /** En Cuenta Corriente solo interesan los que tienen saldo distinto de cero. */
    const visibles = useMemo(() => (
        tab === 'cuenta-corriente' ? clientes.filter(c => c.saldo_cc !== 0) : clientes
    ), [clientes, tab]);

    const totalPages = Math.max(1, Math.ceil(visibles.length / perPage));
    const pageSafe = Math.min(page, totalPages);
    const meta: PaginationMeta = {
        page: pageSafe,
        per_page: perPage,
        total: visibles.length,
        total_pages: totalPages,
        has_next: pageSafe < totalPages,
        has_prev: pageSafe > 1,
    };
    const pagina = useMemo(
        () => visibles.slice((pageSafe - 1) * perPage, pageSafe * perPage),
        [visibles, pageSafe, perPage],
    );

    const totales = useMemo(() => ({
        deuda: clientes.reduce((acc, c) => acc + (c.saldo_cc < 0 ? -c.saldo_cc : 0), 0),
        favor: clientes.reduce((acc, c) => acc + (c.saldo_cc > 0 ? c.saldo_cc : 0), 0),
    }), [clientes]);

    return (
        <div className={styles.cli}>
            <header className={styles.head}>
                <div>
                    <h2 className={styles.headTitle}>Clientes</h2>
                    <p className={styles.headSub}>Base de datos y cuentas corrientes</p>
                </div>
            </header>

            <div className={styles.tabs} role="tablist">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.key}
                        className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {error && <div className={styles.errorBar}>{error}</div>}

            {tab === 'cuenta-corriente' && (
                <section className={styles.kpis}>
                    <div className={styles.kpi}>
                        <span className={styles.kpiLabel}>Deuda de clientes</span>
                        <span className={styles.kpiValue}>{money(totales.deuda)}</span>
                    </div>
                    <div className={styles.kpi}>
                        <span className={styles.kpiLabel}>Saldo a favor</span>
                        <span className={styles.kpiValue}>{money(totales.favor)}</span>
                    </div>
                    <div className={styles.kpi}>
                        <span className={styles.kpiLabel}>Cuentas con saldo</span>
                        <span className={styles.kpiValue}>{visibles.length}</span>
                    </div>
                </section>
            )}

            <div className={styles.layout}>
                <div className={styles.main}>
                    <div className={styles.toolbar}>
                        <div className={styles.searchBox}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                type="search"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Buscar cliente por nombre, email o DNI…"
                                aria-label="Buscar cliente"
                            />
                        </div>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => { setEditando(null); setFormOpen(true); }}
                        >
                            <FaUserPlus /> Nuevo Cliente
                        </button>
                    </div>

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Nombre</th>
                                    <th>Correo</th>
                                    <th>Teléfono</th>
                                    <th className={styles.num}>Saldo CC</th>
                                    <th>Última Compra</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={7} className={styles.empty}>Cargando clientes…</td></tr>
                                )}
                                {!loading && pagina.length === 0 && (
                                    <tr><td colSpan={7} className={styles.empty}>
                                        No hay clientes para mostrar.
                                    </td></tr>
                                )}
                                {!loading && pagina.map(c => (
                                    <tr
                                        key={c.dni}
                                        className={seleccionado?.dni === c.dni ? styles.rowActive : ''}
                                        onClick={() => setSeleccionado(c)}
                                    >
                                        <td className={styles.mono}>#{c.dni}</td>
                                        <td className={styles.strong}>{c.nombre} {c.apellido}</td>
                                        <td className={styles.ellipsis} title={c.email}>{c.email || '—'}</td>
                                        <td className={styles.nowrap}>{c.telefono || '—'}</td>
                                        <td className={`${styles.num} ${c.saldo_cc < 0 ? styles.deuda : ''}`}>
                                            {saldoTexto(c.saldo_cc)}
                                        </td>
                                        <td className={styles.nowrap}>{c.ultima_compra || '—'}</td>
                                        <td>
                                            <div className={styles.acciones}>
                                                <button
                                                    type="button"
                                                    className={styles.iconBtn}
                                                    title="Editar cliente"
                                                    aria-label={`Editar ${c.nombre} ${c.apellido}`}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setEditando(c);
                                                        setFormOpen(true);
                                                    }}
                                                >
                                                    <FaPen />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                                    title="Eliminar cliente"
                                                    aria-label={`Eliminar ${c.nombre} ${c.apellido}`}
                                                    onClick={e => { e.stopPropagation(); eliminarCliente(c); }}
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        meta={meta}
                        onPageChange={setPage}
                        onPerPageChange={setPerPage}
                        itemLabel="clientes"
                    />
                </div>

                <aside className={styles.side}>
                    {seleccionado ? (
                        <section className={styles.ficha}>
                            <div className={styles.fichaHead}>
                                <span className={styles.avatar}>{iniciales(seleccionado)}</span>
                                <strong className={styles.fichaName}>
                                    {seleccionado.nombre} {seleccionado.apellido}
                                </strong>
                                <span className={styles.fichaSub}>
                                    {seleccionado.compras > 0
                                        ? `${seleccionado.compras} compra${seleccionado.compras === 1 ? '' : 's'}`
                                        : 'Sin compras registradas'}
                                </span>
                            </div>

                            <dl className={styles.fichaDatos}>
                                <div>
                                    <dt>Total Compras</dt>
                                    <dd>{money(seleccionado.facturado)}</dd>
                                </div>
                                <div>
                                    <dt>Cuenta Corriente</dt>
                                    <dd className={seleccionado.saldo_cc < 0 ? styles.deuda : ''}>
                                        {saldoTexto(seleccionado.saldo_cc)}
                                    </dd>
                                </div>
                            </dl>

                            <div className={styles.fichaContacto}>
                                <span>{seleccionado.email || 'Sin correo'}</span>
                                <span>{seleccionado.telefono || 'Sin teléfono'}</span>
                                <span>{seleccionado.direccion || 'Sin domicilio'}</span>
                            </div>

                            <button
                                type="button"
                                className={styles.primaryBtn}
                                onClick={() => setMovOpen(true)}
                            >
                                <FaPlus /> Movimiento de cuenta
                            </button>

                            <div className={styles.movimientos}>
                                <span className={styles.movTitulo}>Últimos movimientos</span>
                                {movimientos.length === 0 ? (
                                    <p className={styles.movVacio}>Sin movimientos de cuenta corriente.</p>
                                ) : (
                                    <ul className={styles.movLista}>
                                        {movimientos.slice(0, 8).map(m => (
                                            <li key={m.id} className={styles.movItem}>
                                                <div>
                                                    <strong>{m.tipo}</strong>
                                                    <small>{m.concepto || '—'} · {m.fecha}</small>
                                                </div>
                                                <span className={m.importe < 0 ? styles.deuda : styles.favor}>
                                                    {saldoTexto(m.importe)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </section>
                    ) : (
                        <section className={styles.fichaVacia}>
                            <p>Seleccioná un cliente para ver su ficha y su cuenta corriente.</p>
                        </section>
                    )}
                </aside>
            </div>

            {formOpen && (
                <ClienteModal
                    cliente={editando}
                    onClose={() => { setFormOpen(false); setEditando(null); }}
                    onSave={guardarCliente}
                />
            )}

            {movOpen && seleccionado && (
                <MovimientoModal
                    cliente={seleccionado}
                    onClose={() => setMovOpen(false)}
                    onSave={registrarMovimiento}
                />
            )}

            {toast && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : ''}`}>
                    {toast.text}
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------- modal cliente */

interface ClienteModalProps {
    cliente: Cliente | null;
    onClose: () => void;
    onSave: (form: FormCliente) => void;
}

/**
 * Alta y edicion de cliente.
 *
 * Los obligatorios son los mismos que exige el backend para miembros_familia
 * (DNI, nombre, apellido y email); el resto es opcional.
 */
const ClienteModal: React.FC<ClienteModalProps> = ({ cliente, onClose, onSave }) => {
    const [form, setForm] = useState<FormCliente>(
        cliente
            ? {
                dni: cliente.dni,
                nombre: cliente.nombre,
                apellido: cliente.apellido,
                email: cliente.email,
                telefono: cliente.telefono,
                direccion: cliente.direccion,
                fecha_nacimiento: '',
                sexo: '',
                saldo_cc: '',
            }
            : FORM_VACIO,
    );
    const [error, setError] = useState<string | null>(null);

    const set = (campo: keyof FormCliente) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
            setForm(prev => ({ ...prev, [campo]: e.target.value }));

    const guardar = () => {
        if (!form.dni.trim()) return setError('El DNI es obligatorio.');
        if (!form.nombre.trim()) return setError('El nombre es obligatorio.');
        if (!form.apellido.trim()) return setError('El apellido es obligatorio.');
        if (!form.email.trim()) return setError('El email es obligatorio.');
        setError(null);
        onSave(form);
    };

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Cliente">
                <div className={styles.modalHead}>
                    <div>
                        <h3>{cliente ? 'Editar cliente' : 'Nuevo cliente'}</h3>
                        <p>Los campos marcados con * son obligatorios.</p>
                    </div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">×</button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.fieldRow}>
                        <label className={styles.field}>
                            <span>DNI / CUIT *</span>
                            <input value={form.dni} onChange={set('dni')} inputMode="numeric" />
                        </label>
                        <label className={styles.field}>
                            <span>Email *</span>
                            <input type="email" value={form.email} onChange={set('email')} />
                        </label>
                    </div>

                    <div className={styles.fieldRow}>
                        <label className={styles.field}>
                            <span>Nombre *</span>
                            <input value={form.nombre} onChange={set('nombre')} />
                        </label>
                        <label className={styles.field}>
                            <span>Apellido *</span>
                            <input value={form.apellido} onChange={set('apellido')} />
                        </label>
                    </div>

                    <div className={styles.fieldRow}>
                        <label className={styles.field}>
                            <span>Teléfono</span>
                            <input value={form.telefono} onChange={set('telefono')} />
                        </label>
                        <label className={styles.field}>
                            <span>Domicilio</span>
                            <input value={form.direccion} onChange={set('direccion')} />
                        </label>
                    </div>

                    <div className={styles.fieldRow}>
                        <label className={styles.field}>
                            <span>Fecha de nacimiento</span>
                            <input
                                type="date"
                                value={form.fecha_nacimiento}
                                onChange={set('fecha_nacimiento')}
                            />
                        </label>
                        <label className={styles.field}>
                            <span>Sexo</span>
                            <select value={form.sexo} onChange={set('sexo')}>
                                <option value="">Sin especificar</option>
                                <option value="F">Femenino</option>
                                <option value="M">Masculino</option>
                                <option value="X">Otro</option>
                            </select>
                        </label>
                    </div>

                    {!cliente && (
                        <label className={styles.field}>
                            <span>
                                Saldo inicial de cuenta corriente
                                <em className={styles.fieldHint}> (negativo = el cliente debe)</em>
                            </span>
                            <input
                                type="number"
                                step="0.01"
                                value={form.saldo_cc}
                                onChange={set('saldo_cc')}
                                placeholder="0"
                            />
                        </label>
                    )}

                    {error && <div className={styles.errorBar}>{error}</div>}
                </div>

                <div className={styles.modalFoot}>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>Cancelar</button>
                    <button type="button" className={styles.primaryBtn} onClick={guardar}>Guardar</button>
                </div>
            </div>
        </div>
    );
};

/* --------------------------------------------------------- modal movimiento */

interface MovimientoModalProps {
    cliente: Cliente;
    onClose: () => void;
    onSave: (mov: { tipo: string; importe: string; concepto: string }) => void;
}

const MovimientoModal: React.FC<MovimientoModalProps> = ({ cliente, onClose, onSave }) => {
    const [tipo, setTipo] = useState('Pago');
    const [importe, setImporte] = useState('');
    const [concepto, setConcepto] = useState('');
    const [error, setError] = useState<string | null>(null);

    const guardar = () => {
        const monto = Number(importe);
        if (!monto) return setError('Ingresá un importe distinto de cero.');
        setError(null);
        onSave({ tipo, importe, concepto });
    };

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Movimiento de cuenta">
                <div className={styles.modalHead}>
                    <div>
                        <h3>Movimiento de cuenta corriente</h3>
                        <p>{cliente.nombre} {cliente.apellido} · saldo actual {saldoTexto(cliente.saldo_cc)}</p>
                    </div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">×</button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.fieldRow}>
                        <label className={styles.field}>
                            <span>Tipo</span>
                            <select value={tipo} onChange={e => setTipo(e.target.value)}>
                                <option value="Pago">Pago del cliente (baja la deuda)</option>
                                <option value="Cargo">Cargo / venta a cuenta (sube la deuda)</option>
                                <option value="Ajuste">Ajuste manual</option>
                            </select>
                        </label>
                        <label className={styles.field}>
                            <span>Importe</span>
                            <input
                                type="number"
                                step="0.01"
                                value={importe}
                                onChange={e => setImporte(e.target.value)}
                                placeholder="0,00"
                            />
                        </label>
                    </div>

                    <label className={styles.field}>
                        <span>Concepto</span>
                        <input
                            value={concepto}
                            onChange={e => setConcepto(e.target.value)}
                            placeholder="Ej: pago parcial factura 0003-00000127"
                        />
                    </label>

                    <p className={styles.modalHint}>
                        En <strong>Ajuste</strong> el signo lo definís vos: un importe negativo
                        aumenta la deuda del cliente.
                    </p>

                    {error && <div className={styles.errorBar}>{error}</div>}
                </div>

                <div className={styles.modalFoot}>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>Cancelar</button>
                    <button type="button" className={styles.primaryBtn} onClick={guardar}>Registrar</button>
                </div>
            </div>
        </div>
    );
};

export default Clientes;
